import Box from '@mui/material/Box';
import { OrderUnitOfMeasure, WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Fragment, useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FeatureFlags } from '~constants/featureFlags';
import { Job } from '~hooks/useJob/models';
import { serviceIsHiredTruck } from '~pages/Sales/Orders/newOrderFormSchema';
import { useStores } from '~store';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { shouldAllowUserToAddNewLoads } from '~utils/loads/load-utils';

import HiredTruckLoad from './HiredTruckLoad';

interface HiredTruckLoadsProps {
  isDisabled?: boolean;
  jobData?: Nullable<Job>;
}

const getDefaultHiredTruckLoad = (
  jobData?: Nullable<Job>,
  isStagingWaypointEnabled?: boolean,
) => {
  const stagingWaypoint = isStagingWaypointEnabled
    ? jobData?.waypoints?.find((waypoint) => waypoint.type === WaypointType.STAGING)
    : undefined;

  return {
    quantity: Number(jobData?.equipmentTypeGrossCapacity) || 1,
    unitOfMeasure: Number(jobData?.equipmentTypeGrossCapacity)
      ? (jobData?.unitOfMeasure as ItemNameAndId)
      : ({
          id: OrderUnitOfMeasure.LOAD,
          name: OrderUnitOfMeasure.LOAD,
        } as ItemNameAndId),
    material: jobData?.material
      ? { id: jobData.material.id, name: jobData.material.name }
      : null,
    waypoints: jobData?.waypoints,
    pickUpWaypoints: [
      {
        site:
          jobData?.waypoints?.find((waypoint) => waypoint.type === WaypointType.PICKUP)
            ?.site || null,
        contact:
          jobData?.waypoints?.find((waypoint) => waypoint.type === WaypointType.PICKUP)
            ?.contact || null,
      },
    ],
    dropOffWaypoints: [
      {
        site:
          jobData?.waypoints?.find((waypoint) => waypoint.type === WaypointType.DROP_OFF)
            ?.site || null,
        contact:
          jobData?.waypoints?.find((waypoint) => waypoint.type === WaypointType.DROP_OFF)
            ?.contact || null,
      },
    ],
    stagingWaypoints: [
      {
        site: stagingWaypoint?.site || null,
        contact: stagingWaypoint?.contact || null,
      },
    ],
  };
};

const HiredTruckLoads = ({ isDisabled, jobData }: HiredTruckLoadsProps) => {
  const { control, getValues, setValue } = useFormContext();
  const isHiredTruckService = serviceIsHiredTruck(jobData?.service ?? null);
  const isStagingWaypointEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.stagingWaypoint,
  });

  const { userStore } = useStores();

  const shouldShowAddLoadButton = isDisabled
    ? false
    : jobData
      ? shouldAllowUserToAddNewLoads(jobData, userStore, false)
      : false;

  const {
    fields,
    append: addLoad,
    remove: removeFieldArrayLoad,
  } = useFieldArray({ control, name: 'loads' });

  const removeLoad = useCallback(
    (idx: number) => {
      const loads = getValues('loads');
      const loadId = loads[idx]?.id;

      if (loadId) {
        const deletedLoadIds: string[] = getValues('deletedLoadIds') ?? [];
        setValue('deletedLoadIds', [...deletedLoadIds, loadId], {
          shouldDirty: true,
        });
      }

      removeFieldArrayLoad(idx);
    },
    [getValues, setValue, removeFieldArrayLoad],
  );

  return (
    <Box sx={{ mt: 3 }}>
      {fields.map((load, idx) => (
        <Fragment key={idx}>
          <HiredTruckLoad
            idx={idx}
            isDisabled={isDisabled}
            isHiredTruckService={isHiredTruckService}
            load={load}
            orderId={jobData?.order?.id}
            removeLoad={removeLoad}
          />
        </Fragment>
      ))}

      {shouldShowAddLoadButton && (
        <PseudoLink
          title={`+ ${t('loads.add')}`}
          sx={{ mt: 1, display: 'inline-flex' }}
          action={() => {
            addLoad(getDefaultHiredTruckLoad(jobData, isStagingWaypointEnabled));
          }}
        />
      )}
    </Box>
  );
};

export default HiredTruckLoads;
